import {
  Icon,
  ICON_NAMES,
  MENU_ITEM_SELECTED_INDICATORS,
  Menu,
  MenuItem,
  type MenuItemRenderProps,
} from '@rgrd/component-library'
import { Link, useLocation } from 'react-router'

import { useActiveApp } from '~/shell/app/useActiveApp'
import { useEnabledProducts } from '~/shell/auth/useEnabledProducts'

import styles from './ProductSwitcher.module.css'

/**
 * The product switcher.
 *
 * Lists the sibling products a session may reach, keyed on two independent axes:
 * - The *current* product carries the checkmark.
 * - An *in-suite* product is a same-origin route (an app switch, preserving the customer); a
 *   cross-origin product is a full-page anchor to its configured host.
 *
 * Those are separate because an in-suite product that is not the current one is still same-origin, so "not
 * current" cannot be read as "another host". Scout is the only app this shell serves today, so Arc is the
 * cross-origin case until the merge registers it; when only one product is reachable there is nothing to
 * switch to and the switcher renders nothing.
 *
 * Arc's copy of this component, now identical in shape: the merge deletes one of the two.
 */
export const ProductSwitcher = () => {
  const { pathname } = useLocation()
  const activeApp = useActiveApp()
  const products = useEnabledProducts(pathname, activeApp)

  if (products.length <= 1) {
    return null
  }

  const currentProduct = products.find((product) => product.current)
  const triggerLabel = currentProduct?.name ?? products[0].name

  return (
    <Menu
      label="Switch product"
      align="start"
      triggerClassName={styles.trigger}
      trigger={() => (
        <>
          <span className={styles.triggerLabel}>{triggerLabel}</span>
          <Icon name={ICON_NAMES.ARROWS_CHEVRON_DOWN} size={12} color="grey" />
        </>
      )}
    >
      {products.map((product) =>
        // An in-suite product lives on this origin, so it navigates in-app via a react-router Link, whether or
        // not it is the one being viewed. A cross-origin product is a separately deployed app, reached by the
        // MenuItem's default full-page `<a href>`.
        product.inSuite ? (
          <MenuItem
            key={product.id}
            to={product.href}
            description={product.description}
            selected={product.current}
            selectedIndicator={MENU_ITEM_SELECTED_INDICATORS.check}
            // Named for the same reason as in `ScoutNav`: `MenuItem`'s props are a
            // union (link vs button), which TypeScript will not narrow when typing
            // this callback's own parameters.
            renderItem={({ children, ...anchorProps }: MenuItemRenderProps) => (
              <Link {...anchorProps} to={product.href}>
                {children}
              </Link>
            )}
          >
            {product.name}
          </MenuItem>
        ) : (
          <MenuItem
            key={product.id}
            to={product.href}
            description={product.description}
            selected={product.current}
            selectedIndicator={MENU_ITEM_SELECTED_INDICATORS.check}
          >
            {product.name}
          </MenuItem>
        ),
      )}
    </Menu>
  )
}
